import { useEffect, useState } from "react";
// router
import { useParams, useNavigate } from "react-router";
// redux
import { useSelector } from "react-redux";
// react hook form  and validation
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { editNonDigitalProductSchema } from "../../schemes/product/editNonDigitalProductSchema";
import { Controller } from "react-hook-form";
// mui
import {
    Stack,
    Typography,
    Switch,
    Box,
    FormControlLabel,
} from "@mui/material";
// components
import {
    PageTitle,
    ImagePreview,
    SubmitButton,
    InputField,
    SelectUnit,
    SelectCategory,
    ErrorMessage,
    ThreeDotsLoader,
} from "../../components";
import EnhancedAxios from "../../axios/EnhancedAxios";
import { AxiosResponse, AxiosError } from "axios";
// alerts
import { errorAlert, successAlert } from "../../utils/alerts/alerts";
// types
import { ReduxUserReducerState } from "../../types/global";
// form values types
type FormValues = {
    img: File | null;
    name: string;
    price: number | undefined;
    final_price: number | undefined;
    quantity: number;
    ignore_quantity: boolean;
    description: string;
    categories: any | any[];
    unit: any;
    is_digital: boolean;
    is_disabled: boolean;
};

// page function
const EditNonDigitalProduct = () => {
    const navigate = useNavigate();
    const { productId } = useParams();
    const userToken = useSelector(
        (state: ReduxUserReducerState) => state.user.userToken
    );
    const [submitting, setSubmitting] = useState(false);
    const [defaultImg, setDefaultImg] = useState("");
    const [loading, setLoading] = useState(true);
    // --------------------------- form handle ---------------------------
    const form = useForm<FormValues>({
        defaultValues: {
            img: null,
            name: "",
            price: 0,
            final_price: 0,
            quantity: 0,
            ignore_quantity: false,
            description: "",
            categories: [],
            unit: "",
            is_digital: false,
            is_disabled: false,
        },
        resolver: yupResolver(editNonDigitalProductSchema),
        mode: "all",
    });
    const {
        register,
        setValue,
        formState: { errors },
        handleSubmit,
        reset,
        control,
        watch,
    } = form;
    useEffect(() => {
        setValue("quantity", watch("ignore_quantity") ? 0 : watch("quantity"));
    }, [watch("ignore_quantity")]);
    // submit form
    const onSubmit: SubmitHandler<FormValues> = (data: any) => {
        // reshape categoreis array
        const newCats: any = [];
        data.categories.forEach((cat: any) => {
            newCats.push(cat.id);
        });
        // overwrite new shape of categories to the new DAta
        const newData = { ...data, categories: newCats };
        // convert new data to formData
        const formData = new FormData();
        if (watch("ignore_quantity")) {
            data["quantity"] = 0;
        }
        for (let i in newData) {
            if (i == "categories") {
                newData.categories.forEach((cat: any) => {
                    formData.append("categories", cat);
                });
            } else {
                formData.append(i, newData[i]);
            }
        }
        setSubmitting(true);
        EnhancedAxios("multipart/form-data", userToken)
            .put(`/admin/items/${productId}`, formData)
            .then((res: AxiosResponse) => {
                successAlert(`تم تعديل منتج باسم ${data.name}`);
                setSubmitting(false);
                navigate(-1);
            })
            .catch((error: AxiosError) => {
                errorAlert(error);
            });
    };
    // get product data
    useEffect(() => {
        EnhancedAxios(null, userToken)
            .get(`/admin/items/${productId}`)
            .then((res: AxiosResponse) => {
                setDefaultImg(res.data.img);
                setLoading(false);
                reset({
                    img: res.data.img,
                    name: res.data.name,
                    price: res.data.price,
                    final_price: res.data.final_price,
                    quantity: res.data.quantity,
                    ignore_quantity: res.data.ignore_quantity,
                    description: res.data.description,
                    categories: res.data.categories,
                    unit: JSON.stringify({ id: res.data.unit.id }),
                    is_digital: res.data.is_digital,
                    is_disabled: res.data.is_disabled,
                });
            });
    }, []);
    // while page is loading
    if (loading) return <ThreeDotsLoader />;
    return (
        <Stack gap="20px">
            <PageTitle title="تعديل منتج ملموس" />
            <Stack alignItems="center">
                <Stack
                    width="100%"
                    maxWidth="800px"
                    gap="10px"
                    component="form"
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <ImagePreview
                        setValue={setValue}
                        errorMessage={errors?.img?.message}
                        defaultImage={defaultImg}
                        IPObjectFit="contain"
                    />
                    <InputField
                        type="text"
                        label="اسم المنتج"
                        registration={{ ...register("name") }}
                        errorMessage={errors?.name?.message}
                    />
                    <InputField
                        type="number"
                        label="سعر المنتج"
                        registration={{ ...register("price") }}
                        errorMessage={errors?.price?.message}
                    />
                    <InputField
                        type="number"
                        label="السعر بعد الخصم"
                        registration={{ ...register("final_price") }}
                        errorMessage={errors?.final_price?.message}
                    />
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        gap="10px"
                    >
                        <Box flex="1">
                            <InputField
                                type="number"
                                label="الكمية"
                                registration={{ ...register("quantity") }}
                                errorMessage={errors?.quantity?.message}
                                disabled={
                                    watch("ignore_quantity") ? true : false
                                }
                                styles={{ width: "100%" }}
                            />
                        </Box>
                        <Controller
                            name="ignore_quantity"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            {...field}
                                            checked={watch("ignore_quantity")}
                                            onChange={(e) =>
                                                setValue(
                                                    "ignore_quantity",
                                                    e.target.checked
                                                )
                                            }
                                        />
                                    }
                                    label="تجاهل الكمية"
                                />
                            )}
                        />
                    </Box>
                    <SelectUnit control={control} />
                    {errors?.unit ? (
                        <ErrorMessage errMessage={errors?.unit?.message} />
                    ) : (
                        <></>
                    )}
                    <SelectCategory
                        control={control}
                        watch={watch}
                        setValue={setValue}
                    />
                    {errors?.categories ? (
                        <ErrorMessage
                            errMessage={errors?.categories?.message}
                        />
                    ) : (
                        <></>
                    )}
                    <InputField
                        type="text"
                        label="وصف المنتج"
                        registration={{ ...register("description") }}
                        errorMessage={errors?.description?.message}
                        multiline={true}
                        minRows={3}
                        maxRows={6}
                    />
                    <Controller
                        name="is_disabled"
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                control={
                                    <Switch
                                        {...field}
                                        checked={!watch("is_disabled")}
                                        onChange={(e) =>
                                            setValue(
                                                "is_disabled",
                                                !e.target.checked
                                            )
                                        }
                                    />
                                }
                                label="مفعل"
                            />
                        )}
                    />
                    <SubmitButton label="تعديل" disabled={submitting} />
                </Stack>
            </Stack>
        </Stack>
    );
};

export default EditNonDigitalProduct;
